import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Toast from './Toast';
import ConfirmDialog from './ConfirmDialog';

const ADMIN_TOKEN_KEY = 'metalbox_admin_token';

const AboutUsSettings = () => {
    const [settings, setSettings] = useState({
        ceo_avatar: '',
        ceo_name_zh: '',
        ceo_name_en: '',
        ceo_title_zh: '',
        ceo_title_en: '',
        ceo_bio_zh: '',
        ceo_bio_en: '',
        mission_content_zh: '',
        mission_content_en: '',
        ceo_gallery_images: []
    });
    const [loading, setLoading] = useState(true);
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const [uploadingGallery, setUploadingGallery] = useState(false);
    const [uploadingMission, setUploadingMission] = useState(false);
    const [toast, setToast] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState(null);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await axios.get('/api/about-us-settings');
            if (response.data.success) {
                const data = response.data.settings;
                // Parse gallery images if string
                if (typeof data.ceo_gallery_images === 'string') {
                    try {
                        data.ceo_gallery_images = JSON.parse(data.ceo_gallery_images);
                    } catch (e) {
                        data.ceo_gallery_images = [];
                    }
                }
                setSettings(data);
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            setToast({ message: '获取设置失败', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const getImageUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        // Ensure we use the main domain, not admin subdomain
        const origin = window.location.origin.replace('admin.', '');
        return `${origin}${path}`;
    };

    const handleAvatarUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);

        setUploadingAvatar(true);
        try {
            const token = localStorage.getItem(ADMIN_TOKEN_KEY);
            const response = await axios.post('/api/admin/about-us-settings/upload-ceo-avatar', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.data.success) {
                setSettings(prev => ({ ...prev, ceo_avatar: response.data.imagePath }));
                setToast({ message: 'CEO头像上传成功', type: 'success' });
            }
        } catch (error) {
            console.error('Error uploading avatar:', error);
            setToast({ message: '上传失败: ' + (error.response?.data?.message || error.message), type: 'error' });
        } finally {
            setUploadingAvatar(false);
        }
    };

    const handleGalleryUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);

        setUploadingGallery(true);
        try {
            const token = localStorage.getItem(ADMIN_TOKEN_KEY);
            const response = await axios.post('/api/admin/about-us-settings/upload-ceo-gallery', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.data.success) {
                setSettings(prev => ({
                    ...prev,
                    ceo_gallery_images: [...prev.ceo_gallery_images, response.data.imagePath]
                }));
                setToast({ message: '画廊图片上传成功', type: 'success' });
            }
        } catch (error) {
            console.error('Error uploading gallery image:', error);
            setToast({ message: '上传失败: ' + (error.response?.data?.message || error.message), type: 'error' });
        } finally {
            setUploadingGallery(false);
        }
    };

    const handleDeleteGalleryImage = async (imagePath) => {
        setConfirmDialog({
            message: '确定要删除这张图片吗？',
            onConfirm: async () => {
                try {
                    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
                    await axios.delete('/api/admin/about-us-settings/ceo-gallery', {
                        headers: { Authorization: `Bearer ${token}` },
                        data: { imagePath }
                    });

                    setSettings(prev => ({
                        ...prev,
                        ceo_gallery_images: prev.ceo_gallery_images.filter(img => img !== imagePath)
                    }));
                    setToast({ message: '图片已删除', type: 'success' });
                } catch (error) {
                    console.error('Error deleting image:', error);
                    setToast({ message: '删除失败', type: 'error' });
                } finally {
                    setConfirmDialog(null);
                }
            },
            onCancel: () => setConfirmDialog(null)
        });
    };

    const handleMissionImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);

        setUploadingMission(true);
        try {
            const token = localStorage.getItem(ADMIN_TOKEN_KEY);
            const response = await axios.post('/api/admin/about-us-settings/upload-mission-image', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.data.success) {
                setSettings(prev => ({
                    ...prev,
                    mission_images: [...(prev.mission_images || []), response.data.imagePath]
                }));
                setToast({ message: '使命图片上传成功', type: 'success' });
            }
        } catch (error) {
            console.error('Error uploading mission image:', error);
            setToast({ message: '上传失败: ' + (error.response?.data?.message || error.message), type: 'error' });
        } finally {
            setUploadingMission(false);
        }
    };

    const handleDeleteMissionImage = async (imagePath) => {
        setConfirmDialog({
            message: '确定要删除这张图片吗？',
            onConfirm: async () => {
                try {
                    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
                    await axios.delete('/api/admin/about-us-settings/mission-image', {
                        headers: { Authorization: `Bearer ${token}` },
                        data: { imagePath }
                    });

                    setSettings(prev => ({
                        ...prev,
                        mission_images: (prev.mission_images || []).filter(img => img !== imagePath)
                    }));
                    setToast({ message: '图片已删除', type: 'success' });
                } catch (error) {
                    console.error('Error deleting image:', error);
                    setToast({ message: '删除失败', type: 'error' });
                } finally {
                    setConfirmDialog(null);
                }
            },
            onCancel: () => setConfirmDialog(null)
        });
    };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem(ADMIN_TOKEN_KEY);
            const response = await axios.put('/api/admin/about-us-settings', {
                ceo_name_zh: settings.ceo_name_zh,
                ceo_name_en: settings.ceo_name_en,
                ceo_title_zh: settings.ceo_title_zh,
                ceo_title_en: settings.ceo_title_en,
                ceo_bio_zh: settings.ceo_bio_zh,
                ceo_bio_en: settings.ceo_bio_en,
                mission_content_zh: settings.mission_content_zh,
                mission_content_en: settings.mission_content_en,
                ceo_gallery_images: settings.ceo_gallery_images,
                mission_images: settings.mission_images || []
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setToast({ message: '设置已保存', type: 'success' });
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            setToast({ message: '保存失败', type: 'error' });
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f5a623]"></div>
        </div>
    );

    return (
        <div className="p-6">
            <h2 className="text-3xl font-bold mb-6 text-white">About Us 页面设置</h2>

            {/* CEO Section */}
            <div className="mb-8 bg-[#1a1d17] border border-[#2a2d27] p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-6 text-white border-b border-[#2a2d27] pb-4">CEO 信息</h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Avatar */}
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">CEO 头像</label>
                        <div className="relative w-48 h-48 bg-[#262a22] rounded-lg overflow-hidden mb-4 border border-[#2a2d27]">
                            {settings.ceo_avatar ? (
                                <img
                                    src={getImageUrl(settings.ceo_avatar)}
                                    alt="CEO Avatar"
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="flex items-center justify-center h-full text-gray-500">无图片</div>
                            )}
                        </div>
                        <div className="relative">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleAvatarUpload}
                                className="hidden"
                                id="ceo-avatar-upload"
                                disabled={uploadingAvatar}
                            />
                            <label
                                htmlFor="ceo-avatar-upload"
                                className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${uploadingAvatar
                                    ? 'bg-[#2a2d27] text-gray-400 cursor-not-allowed'
                                    : 'bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white'
                                    }`}
                            >
                                {uploadingAvatar ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        上传中...
                                    </>
                                ) : (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256" className="mr-2">
                                            <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,172l52-52,80,80H40Zm176,28H194.63l-36-36,20-20L216,181.38V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"></path>
                                        </svg>
                                        更换头像
                                    </>
                                )}
                            </label>
                        </div>
                    </div>

                    {/* Info (Editable) */}
                    <div className="md:col-span-2 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">姓名 (中文)</label>
                                <input
                                    type="text"
                                    value={settings.ceo_name_zh}
                                    onChange={(e) => setSettings({ ...settings, ceo_name_zh: e.target.value })}
                                    className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Name (English)</label>
                                <input
                                    type="text"
                                    value={settings.ceo_name_en}
                                    onChange={(e) => setSettings({ ...settings, ceo_name_en: e.target.value })}
                                    className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">职务 (中文)</label>
                                <input
                                    type="text"
                                    value={settings.ceo_title_zh}
                                    onChange={(e) => setSettings({ ...settings, ceo_title_zh: e.target.value })}
                                    className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Title (English)</label>
                                <input
                                    type="text"
                                    value={settings.ceo_title_en}
                                    onChange={(e) => setSettings({ ...settings, ceo_title_en: e.target.value })}
                                    className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">简介 (中文)</label>
                            <textarea
                                value={settings.ceo_bio_zh}
                                onChange={(e) => setSettings({ ...settings, ceo_bio_zh: e.target.value })}
                                rows={3}
                                className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623] mb-2 resize-none"
                            />
                            <label className="block text-sm font-medium text-gray-400 mb-1">Bio (English)</label>
                            <textarea
                                value={settings.ceo_bio_en}
                                onChange={(e) => setSettings({ ...settings, ceo_bio_en: e.target.value })}
                                rows={3}
                                className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623] resize-none"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* CEO Gallery */}
            <div className="mb-8 bg-[#1a1d17] border border-[#2a2d27] p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-6 text-white border-b border-[#2a2d27] pb-4">CEO 画廊图片</h3>
                <div className="grid grid-cols-4 md:grid-cols-8 gap-4 mb-4">
                    {settings.ceo_gallery_images.map((path, index) => (
                        <div key={index} className="relative group aspect-square bg-[#262a22] rounded-lg overflow-hidden border border-[#2a2d27]">
                            <img
                                src={getImageUrl(path)}
                                alt={`Gallery ${index}`}
                                className="w-full h-full object-cover"
                            />
                            <button
                                onClick={() => handleDeleteGalleryImage(path)}
                                className="absolute top-2 right-2 p-2 bg-red-500/80 hover:bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 256 256">
                                    <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"></path>
                                </svg>
                            </button>
                        </div>
                    ))}
                    <div className="aspect-square border-2 border-dashed border-[#2a2d27] rounded-lg flex items-center justify-center hover:border-[#4a5f3a] transition-colors bg-[#1a1d17]">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleGalleryUpload}
                            className="hidden"
                            id="gallery-upload"
                            disabled={uploadingGallery}
                        />
                        <label htmlFor="gallery-upload" className="cursor-pointer flex flex-col items-center text-gray-500 hover:text-[#4a5f3a] w-full h-full justify-center">
                            {uploadingGallery ? (
                                <svg className="animate-spin h-8 w-8 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 256 256" className="mb-2">
                                    <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
                                </svg>
                            )}
                            <span className="text-sm">{uploadingGallery ? '上传中...' : '添加图片'}</span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Mission Section */}
            <div className="mb-8 bg-[#1a1d17] border border-[#2a2d27] p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-6 text-white border-b border-[#2a2d27] pb-4">使命内容 (Mission)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">中文内容</label>
                        <textarea
                            rows={6}
                            value={settings.mission_content_zh}
                            onChange={(e) => setSettings({ ...settings, mission_content_zh: e.target.value })}
                            className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">English Content</label>
                        <textarea
                            rows={6}
                            value={settings.mission_content_en}
                            onChange={(e) => setSettings({ ...settings, mission_content_en: e.target.value })}
                            className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                        />
                    </div>
                </div>
            </div>

            {/* Mission Images Section */}
            <div className="mb-8 bg-[#1a1d17] border border-[#2a2d27] p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-6 text-white border-b border-[#2a2d27] pb-4">使命图片 (Mission Images)</h3>
                <div className="grid grid-cols-4 md:grid-cols-8 gap-4 mb-4">
                    {(settings.mission_images || []).map((path, index) => (
                        <div key={index} className="relative group aspect-square bg-[#262a22] rounded-lg overflow-hidden border border-[#2a2d27]">
                            <img
                                src={getImageUrl(path)}
                                alt={`Mission ${index}`}
                                className="w-full h-full object-cover"
                            />
                            <button
                                onClick={() => handleDeleteMissionImage(path)}
                                className="absolute top-2 right-2 p-2 bg-red-500/80 hover:bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 256 256">
                                    <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"></path>
                                </svg>
                            </button>
                        </div>
                    ))}
                    <div className="aspect-square border-2 border-dashed border-[#2a2d27] rounded-lg flex items-center justify-center hover:border-[#4a5f3a] transition-colors bg-[#1a1d17]">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleMissionImageUpload}
                            className="hidden"
                            id="mission-upload"
                            disabled={uploadingMission}
                        />
                        <label htmlFor="mission-upload" className="cursor-pointer flex flex-col items-center text-gray-500 hover:text-[#4a5f3a] w-full h-full justify-center">
                            {uploadingMission ? (
                                <svg className="animate-spin h-8 w-8 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 256 256" className="mb-2">
                                    <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
                                </svg>
                            )}
                            <span className="text-sm">{uploadingMission ? '上传中...' : '添加图片'}</span>
                        </label>
                    </div>
                </div>
            </div>

            <div className="flex justify-end">
                <button
                    onClick={handleSave}
                    className="bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white px-8 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256">
                        <path d="M229.66,49.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,164.69,218.34,38.34a8,8,0,0,1,11.32,11.32Z"></path>
                    </svg>
                    保存更改
                </button>
            </div>

            {/* Toast */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            {/* Confirm Dialog */}
            {confirmDialog && <ConfirmDialog {...confirmDialog} />}
        </div>
    );
};

export default AboutUsSettings;
